<script setup>
  import {computed, ref} from "vue";

  import ComponentIconButton from "@components/Buttons/IconButton.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentRecordEasyComDialog from "@components/Dialogs/RecordEasyComDialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {router} from "@inertiajs/vue3";
  import {MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "ComponentRecordProgress",
  });

  defineExpose({easyCom});

  const props = defineProps({
    record: {
      type: Object,
      default: () => ({}),
    },
    transfer: {
      type: Object,
      default: null,
    },
    forceDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Only show the single easy-com button
     */
    showEasyComOnlyButton: {
      type: Boolean,
      default: false,
    },
    /**
     * Clicks on easy-com button must be handled
     * differently when on result page
     */
    isOnResultPage: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["open:recordCaseCloseDialog"]);

  const refRecordEasyComDialog = ref(null);
  const refHowToCaseCloseDialog = ref(null);

  const easyInTooltip = computed(() => {
    return is("formIsEmpty") ? "„Easy in“ Eingabemaske; in Bearbeitung" : "„Easy in“ Eingabemaske; bearbeitet";
  });

  const easyOutTooltip = computed(() => {
    if (props.record.show_url === null) {
      return "„Easy out“; noch nicht analysiert";
    }

    if (is("resultHasBeenCreated")) {
      return "„Easy out“ Analyseergebnis; in Bearbeitung";
    }
    if (is("resultHasBeenCommunicated")) {
      return "„Easy out“ Analyseergebnis; bearbeitet";
    }

    return "„Easy out“ Analyseergebnis";
  });

  const easyComTooltip = computed(() => {
    if (props.record.show_url === null) {
      return "„Easy com“; noch nicht analysiert";
    }

    if (is("resultHasBeenCommunicated") && props.record.status !== "completed" && !props.record.closed) {
      return "„Easy com“ Kommunikation; in Bearbeitung";
    }
    if (props.record.status === "completed" || props.record.closed) {
      return "„Easy com“ Kommunikation; bearbeitet";
    }
    return "„Easy com“ Kommunikation";
  });

  function is(progress) {
    return props.record?.progress === progress;
  }

  function easyIn() {
    if (props.record.edit_url !== null) {
      router.visit(props.record.edit_url);
    }
  }

  function easyOut() {
    if (props.record.show_url !== null) {
      router.visit(props.record.show_url);
    }
  }

  function easyCom() {
    if (props.record.is_pos_record === false) {
      refRecordEasyComDialog.value.open(props.record);
    } else {
      // it's a b̶o̶y̶ pos record...

      if (props.showEasyComOnlyButton || props.isOnResultPage) {
        // when this RecordProgress button has been clicked at records.show page,
        // we emit an open event to open the real "RecordCaseCloseDialog"
        emit("open:recordCaseCloseDialog");
      } else {
        // otherwise this button has been clicked elsewhere... we just
        // show the information, that closing a pos case is only possible
        // when then records.show page is open
        refHowToCaseCloseDialog.value.open();
      }
    }
  }
</script>

<template>
  <div :class="{'relative z-0 flex w-[100px] items-center justify-between': !showEasyComOnlyButton}">
    <!-- background lines: start -->
    <div
      v-if="!showEasyComOnlyButton"
      :class="
        cn('absolute top-1/2 h-[3px] w-1/2', {
          'bg-gray-300': forceDisabled || is('default') || is('formIsEmpty') || is('formIsFilled'),
          'bg-mcred': !forceDisabled && (is('resultHasBeenCreated') || is('resultHasBeenCommunicated')),
        })
      "
    />

    <div
      v-if="!showEasyComOnlyButton"
      :class="
        cn('absolute top-1/2 right-0 z-0 h-[3px] w-1/2', {
          'bg-gray-300':
            forceDisabled || is('default') || is('formIsEmpty') || is('formIsFilled') || is('resultHasBeenCreated'),
          'bg-mcred': !forceDisabled && (is('resultHasBeenCommunicated') || record?.closed),
        })
      "
    />
    <!-- background lines: end -->

    <!-- easy in button -->
    <!-- TODO REWORK THIS WTF -->
    <MedTooltip v-if="!showEasyComOnlyButton" :content="easyInTooltip" :disabled="forceDisabled">
      <ComponentIconButton
        icon="record_voice_over"
        :class="
          cn('relative z-10 size-7.5 text-base enabled:cursor-pointer disabled:cursor-not-allowed', {
            'border-gray-200 bg-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-300': is('default'),
            'p4umc-primary':
              (is('formIsEmpty') ||
                is('formIsFilled') ||
                is('resultHasBeenCreated') ||
                is('resultHasBeenCommunicated')) &&
              !forceDisabled,
          })
        "
        :disabled="forceDisabled || record.edit_url === null"
        :test-id="getTestId('record-progress-easy-in')"
        @click="easyIn"
      />
    </MedTooltip>

    <!-- easy out button -->
    <MedTooltip v-if="!showEasyComOnlyButton" :content="easyOutTooltip" :disabled="forceDisabled">
      <ComponentIconButton
        :class="
          cn('relative z-10 size-7.5 text-base enabled:cursor-pointer disabled:cursor-not-allowed', {
            'border-gray-200 bg-gray-200 text-gray-500 enabled:hover:border-gray-300 enabled:hover:bg-gray-300':
              is('default') || is('formIsEmpty') || is('formIsFilled'),
            'p4umc-primary': (is('resultHasBeenCreated') || is('resultHasBeenCommunicated')) && !forceDisabled,
          })
        "
        :disabled="forceDisabled || record.show_url === null"
        :test-id="getTestId('record-progress-easy-out')"
        @click="easyOut"
      >
        <svg class="size-4.5 fill-current" viewBox="0 0 24 24">
          <path
            d="M20,11H23V13H20V11M1,11H4V13H1V11M13,1V4H11V1H13M4.92,3.5L7.05,5.64L5.63,7.05L3.5,4.93L4.92,3.5M16.95,5.63L19.07,3.5L20.5,4.93L18.37,7.05L16.95,5.63M12,6A6,6 0 0,1 18,12C18,14.22 16.79,16.16 15,17.2V19A1,1 0 0,1 14,20H10A1,1 0 0,1 9,19V17.2C7.21,16.16 6,14.22 6,12A6,6 0 0,1 12,6M14,21V22A1,1 0 0,1 13,23H11A1,1 0 0,1 10,22V21H14M11,18H13V15.87C14.73,15.43 16,13.86 16,12A4,4 0 0,0 12,8A4,4 0 0,0 8,12C8,13.86 9.27,15.43 11,15.87V18Z"
          />
        </svg>
      </ComponentIconButton>
    </MedTooltip>

    <!-- easy com button -->
    <MedTooltip :content="easyComTooltip" :disabled="forceDisabled">
      <ComponentIconButton
        icon="sentiment_satisfied_alt"
        :class="
          cn('relative z-10 size-7.5 enabled:cursor-pointer disabled:cursor-not-allowed', {
            'text-base': !showEasyComOnlyButton,
            'size-10.5 p-1 text-3xl': showEasyComOnlyButton,
            'border-gray-200 bg-gray-200 text-gray-500 enabled:hover:border-gray-300 enabled:hover:bg-gray-200':
              is('default') || is('formIsEmpty') || is('formIsFilled'),
            'border-2 border-mcred hover:border-mcred': is('resultHasBeenCommunicated'),
            'p4umc-primary': (record.status === 'completed' || record?.closed) && !forceDisabled,
          })
        "
        :disabled="forceDisabled || record.show_url === null"
        :test-id="getTestId('record-progress-easy-com')"
        @click="easyCom"
      />
    </MedTooltip>
  </div>

  <ComponentRecordEasyComDialog ref="refRecordEasyComDialog" :record="record" />

  <ComponentDialog
    ref="refHowToCaseCloseDialog"
    :dialog-title="
      transfer?.has_pos_documents?.doctor_pdf || transfer?.has_pos_documents?.patient_pdf
        ? ''
        : 'Easy com - Kommunikation'
    "
    dialog-width="medium"
    dialog-type="information"
    :test-id="getTestId('record-progress-how-to-case-close')"
  >
    <template #content>
      <template v-if="transfer?.has_pos_documents?.doctor_pdf || transfer?.has_pos_documents?.patient_pdf">
        <h2 class="flex w-full justify-center space-x-2 text-mcgreen">
          <ComponentIcon class="mt-1 text-xl">check</ComponentIcon>
          <span class="text-xl">Dokumente erfolgreich gespeichert</span>
        </h2>

        <p class="mt-2 text-center">Folgende PDF-Dokumente wurden bereits in der Warenwirtschaft gespeichert:</p>

        <ul class="mx-auto mt-2 w-32 list-disc">
          <li v-if="transfer?.has_pos_documents?.doctor_pdf">Arzt-PDF</li>
          <li v-if="transfer?.has_pos_documents?.patient_pdf">Patient-PDF</li>
          <li v-if="transfer?.has_pos_documents?.full_pdf">Archiv-PDF</li>
        </ul>

        <hr class="mt-4 h-px w-full bg-gray-500 p-0" />

        <p class="mt-4">Möchten Sie PDF-Dokumente in der Warenwirtschaft aktualisieren?</p>

        <p class="mt-4">
          Öffnen Sie das Analyseergebnis und klicken Sie unten auf „Dokumente gespeichert“, um diese erneut in der
          Warenwirtschaft zu speichern.
        </p>
      </template>

      <template v-else>
        <p>Möchten Sie PDF-Dokumente in der Warenwirtschaft speichern?</p>

        <p class="mt-4">
          Öffnen Sie das Analyseergebnis und klicken Sie unten auf „Weiter“, um Dokumente in der Warenwirtschaft zu
          speichern.
        </p>
      </template>
    </template>
  </ComponentDialog>
</template>
